'use client';

import React from 'react';
import { FEATURES } from '@/lib/landing-constants';

const Features: React.FC = () => {
  return (
    <section id="features" className="py-24 bg-white relative">
      <div className="absolute top-0 inset-x-0 h-px bg-gray-100"></div>

      <div className="container mx-auto px-6">

        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <span className="text-violet-600 font-bold tracking-wider uppercase text-xs bg-violet-50 px-3 py-1 rounded-full border border-violet-100">Powerful Features</span>
          <h2 className="text-3xl md:text-5xl font-display font-bold text-gray-900 mt-6 mb-6">
            Everything You Need to <br/>Drive Engagement
          </h2>
          <p className="text-gray-600 text-lg leading-relaxed">
            Stop relying on boring email blasts. Transform customer interactions into exciting gamified experiences that drive real revenue.
          </p>
        </div>

        {/* How it works steps - QR Flow */}
        <div className="mb-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                step: 1,
                title: "Customer scans",
                desc: "Place QR codes on receipts, packaging, or posters.",
                icon: (
                  <svg className="h-6 w-6 text-violet-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h3v3H7V7zm7 0h3v3h-3V7zM7 14h3v3H7v-3zm7 7h-4m7-4v-3m0-4V7m-4 0h-4M7 17H4v-4m0-2V7m17 10h-4" />
                  </svg>
                ),
              },
              {
                step: 2,
                title: "Register & play",
                desc: "A friendly signup, then instant game access.",
                icon: (
                  <svg className="h-6 w-6 text-fuchsia-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
              },
              {
                step: 3,
                title: "Points & rewards",
                desc: "Turn engagement into loyalty with points and perks.",
                icon: (
                  <svg className="h-6 w-6 text-amber-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 17l-4 2 1-4-3-3 4-.5L12 8l2 3.5 4 .5-3 3 1 4-4-2z" />
                  </svg>
                ),
              },
              {
                step: 4,
                title: "You get insight",
                desc: "Capture customer data and learn what converts.",
                icon: (
                  <svg className="h-6 w-6 text-emerald-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 19h16M7 16V9m5 7V6m5 10v-8" />
                  </svg>
                ),
              },
            ].map((item) => (
              <div
                key={item.step}
                className="group relative rounded-3xl border border-gray-100 bg-white p-6 shadow-sm hover:shadow-xl hover:shadow-violet-500/10 transition-all duration-300 overflow-hidden"
              >
                <div className="relative flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center">
                      {item.icon}
                    </div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-gray-50 border border-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">
                      <span className="text-violet-700 font-bold">Step {item.step}</span>
                    </div>
                  </div>
                </div>

                <h3 className="relative mt-5 text-lg font-bold text-gray-900">{item.title}</h3>
                <p className="relative mt-2 text-sm text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {FEATURES.map((feature) => (
            <div
              key={feature.id}
              className="group p-8 rounded-3xl bg-gray-50 border border-gray-100 hover:bg-white hover:border-violet-100 hover:shadow-xl hover:shadow-violet-500/5 transition-all duration-300 relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-white/0 to-white/0 group-hover:from-violet-500/5 group-hover:to-fuchsia-500/5 rounded-bl-full transition-all duration-500"></div>

              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 ${feature.color} group-hover:scale-110 transition-transform duration-300`}>
                {feature.icon}
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">{feature.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
